import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Input, Form, message, Drawer, Layout, Tabs, Spin, Modal, Radio } from 'antd';
import { Button as CrceButton, Checkbox as CrceCheckbox } from '@crc-evans/ui-components';
import {
    Sun,
    Cloud,
    CloudSun,
    CloudRain,
    CloudLightning,
    CloudSnow,
    CloudFog,
    Zap,
    X,
    Loader2,
    CheckCircle2,
    PenLine,
    FileText,
} from 'lucide-react';
import DSRSignaturePad from './DSRSignaturePad';
import { connect } from 'react-redux';
import { createDsrLog, submitDsrLog, updateDsrLogSections } from '../../redux/dsr/action';
import { get } from 'lodash';
import moment from 'moment';
import { getJwtToken } from '../../services/auth';
import './DSRReportDetails.css';
import './DownTimeModal.css';
import '../GenerateReport.css';

const { Option } = Select;
const { TextArea } = Input;
const { Content, Footer } = Layout;
const { TabPane } = Tabs;

const WEATHER_ICON_SIZE = 18;

const weatherOptions = [
    { value: '1', label: 'Sunny', Icon: Sun, iconColor: '#eab308' },
    { value: '2', label: 'Fair', Icon: Sun, iconColor: '#22c55e' },
    { value: '3', label: 'Partly Cloudy', Icon: CloudSun, iconColor: '#64748b' },
    { value: '4', label: 'Mostly Cloudy', Icon: Cloud, iconColor: '#475569' },
    { value: '5', label: 'Cloudy', Icon: Cloud, iconColor: '#334155' },
    { value: '6', label: 'Haze/Fog', Icon: CloudFog, iconColor: '#94a3b8' },
    { value: '7', label: 'Rainy', Icon: CloudRain, iconColor: '#3b82f6' },
    { value: '8', label: 'Rainy / Thunderstorm', Icon: CloudLightning, iconColor: '#7c3aed' },
    { value: '9', label: 'Thunderstorm', Icon: Zap, iconColor: '#ea580c' },
    { value: '10', label: 'Snow / Ice', Icon: CloudSnow, iconColor: '#94a3b8' },
];

const reportSections = [
    { key: 'technician', title: 'Technician Log', skipKey: 'isTechnicianLogSkipped' },
    { key: 'ehs', title: 'EHS Issues & Corrective Actions', skipKey: 'isEhsSkipped' },
    { key: 'equipment', title: 'Equipment Usage', skipKey: 'isEquipmentUsageSkipped' },
    { key: 'material', title: 'Material Consumption', skipKey: 'isMaterialUsageSkipped' },
    { key: 'consumables', title: 'Consumables & PPE Usage', skipKey: 'isConsumablesSkipped' },
    { key: 'downtime', title: 'Down Time', skipKey: 'isDowntimeSkipped' },
    { key: 'production', title: 'Production Joints', skipKey: 'isProductionJointSkipped' }
];

const DSRReportDetails = ({
    onSubmit,
    onSkip,
    onSectionSelect,
    activeShift,
    setActiveShift,
    submittedSections = {},
    skipped = { day: false, night: false, all: false },
    setSkipped,
    selectedProject,
    selectedMainline,
    selectedDate,
    isSubmitted: parentIsSubmitted,
    // Redux props
    onCreateDsrLog,
    onSubmitDsrLog,
    onUpdateDsrLogSections,
    isCreatingDsrLog,
    isSubmittingDsrLog,
    isUpdatingDsrLogSections,
    currentDsrLog,

}) => {
    const [selectedDateValue, setSelectedDateValue] = useState(selectedDate ? moment(selectedDate) : moment());
    const [weather, setWeather] = useState();
    const [dayShiftWeather, setDayShiftWeather] = useState();
    const [nightShiftWeather, setNightShiftWeather] = useState();
    const [newWeather, setNewWeather] = useState('');
    const [form] = Form.useForm();
    const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
    const [signatureType, setSignatureType] = useState(null);
    const [signatureData, setSignatureData] = useState({});
    const [signatureSaveLoading, setSignatureSaveLoading] = useState(false);
    const [dayShiftComments, setDayShiftComments] = useState('');
    const [nightShiftComments, setNightShiftComments] = useState('');
    const [dsrLogId, setDsrLogId] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [updatingSection, setUpdatingSection] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [isReportGenerated, setIsReportGenerated] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    // Single "Generate Report" modal state
    const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState('pdf'); // 'flash' | 'pdf' | 'combined'
    const [emailInput, setEmailInput] = useState('');
    const [emailList, setEmailList] = useState([]);

    // Combined report: always selectable; API validates projectId.
    const [isGeneratingCombined, setIsGeneratingCombined] = useState(false);
    const canGenerateCombinedReport = true;

    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        const pt = selectedProject?.projectType ?? selectedProject?.project_type;
        // eslint-disable-next-line no-console
        console.info('[DSR combined report]', {
            projectId: selectedProject?.id,
            projectType: pt,
            combinedOptionEnabled: canGenerateCombinedReport,
        });
    }, [selectedProject?.id, selectedProject?.projectType, selectedProject?.project_type, canGenerateCombinedReport]);

    // Use parent isSubmitted if available, otherwise use local state
    const finalIsSubmitted = parentIsSubmitted !== undefined ? parentIsSubmitted : isSubmitted;

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    // Centralized skip handler for all sections - same logic as main DSR component
    const handleSectionSkipChange = (section, checked) => {
        if (!currentDsrLog?.id) return;

        // Set the updating section to show loading on this specific card
        setUpdatingSection(section);

        // Store the previous state for potential rollback
        const previousSectionsSkipped = { ...currentDsrLog.sectionsSkipped };

        // Optimistic update - update the state immediately
        const currentSectionsSkipped = currentDsrLog.sectionsSkipped || {};
        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';

        let skipKey;
        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                setUpdatingSection(null);
                return;
        }

        // Optimistically update the currentDsrLog state
        const updatedSectionsSkipped = {
            ...currentSectionsSkipped,
            [skipKey]: checked ? 1 : 0
        };

        // Update the local state immediately for optimistic UI update
        if (currentDsrLog) {
            currentDsrLog.sectionsSkipped = updatedSectionsSkipped;
        }

        // Generate submission status - if section is being skipped, mark as submitted
        const submissionStatus = generateSubmissionStatus(section, activeShift, checked);

        // Call API in the background
        onUpdateDsrLogSections({
            dsrLogId: currentDsrLog.id,
            sectionsSkipped: updatedSectionsSkipped,
            submissionStatus: submissionStatus,
            previousState: previousSectionsSkipped // Store for potential rollback
        });
    };

    // Get section skip state - same logic as main DSR component
    const getSectionSkipState = (section) => {
        if (!currentDsrLog?.sectionsSkipped) return false;

        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';
        let skipKey;

        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                return false;
        }

        return currentDsrLog.sectionsSkipped[skipKey] === 1;
    };

    // Get section submitted status from currentDsrLog API response
    const getSectionSubmittedState = (section) => {
        if (!currentDsrLog) return false;

        const shiftKeySuffix = activeShift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return false;
        }

        return currentDsrLog[submittedKey] === 1 || currentDsrLog[submittedKey] === true;
    };

    // Clear updating section when Redux state changes
    useEffect(() => {
        if (!isUpdatingDsrLogSections && updatingSection) {
            setUpdatingSection(null);
        }
    }, [isUpdatingDsrLogSections, updatingSection]);

    // Force re-render when currentDsrLog sectionsSkipped changes
    useEffect(() => {
        if (currentDsrLog?.sectionsSkipped) {
    // Force re-render to update skip states
            setUpdatingSection(prev => prev);
        }
    }, [currentDsrLog?.sectionsSkipped]);

    // Update selectedDateValue when selectedDate prop changes
    useEffect(() => {
        if (selectedDate) {
            // Reset all form values before loading new data
            setSelectedDateValue(selectedDate);
            setWeather(null);
            setDayShiftWeather(null);
            setNightShiftWeather(null);
            setDayShiftComments('');
            setNightShiftComments('');
            setSignatureData({ manager: null, client: null });
            setDsrLogId(null);
            setIsSubmitted(false);
            setUpdatingSection(null);
            setValidationErrors({});
            setIsReportGenerated(false);
            setPdfUrl('');
            form.resetFields();
        }
    }, [selectedDate]);

    // Initialize DSR log when component mounts or dependencies change
    useEffect(() => {
        if (selectedProject && selectedMainline && selectedDateValue) {
            const logDate = selectedDateValue.format('YYYY-MM-DD');
            onCreateDsrLog({
                projectId: selectedProject.id,
                logDate: logDate,
                sowId: selectedMainline.id
            });
        }
    }, [selectedProject, selectedMainline, selectedDateValue]);

    // Update local state when DSR log is created
    useEffect(() => {
        if (currentDsrLog) {
            const isNewOrChangedLog = !dsrLogId || dsrLogId !== currentDsrLog.id;

            if (isNewOrChangedLog) {
                setDsrLogId(currentDsrLog.id);
                // Hydrate weather from API only when switching logs — not on every Redux refresh,
                // otherwise clearing the field is immediately overwritten.
                setDayShiftWeather(
                    currentDsrLog.weatherStatusDayshift
                        ? currentDsrLog.weatherStatusDayshift.toString()
                        : undefined
                );
                setNightShiftWeather(
                    currentDsrLog.weatherStatusNightshift
                        ? currentDsrLog.weatherStatusNightshift.toString()
                        : undefined
                );
                if (activeShift === 'day') {
                    setWeather(
                        currentDsrLog.weatherStatusDayshift
                            ? currentDsrLog.weatherStatusDayshift.toString()
                            : undefined
                    );
                } else {
                    setWeather(
                        currentDsrLog.weatherStatusNightshift
                            ? currentDsrLog.weatherStatusNightshift.toString()
                            : undefined
                    );
                }
            }

            // Check if DSR is already submitted
            if (currentDsrLog.logSubmitDate) {
                setIsSubmitted(true);
            } else {
                setIsSubmitted(false);
            }

            // Pre-populate form with existing data
            if (currentDsrLog.managerName) {
                form.setFieldsValue({
                    managerName: currentDsrLog.managerName,
                    clientName: currentDsrLog.clientName
                });
            }

            // Set existing comments
            if (currentDsrLog.dsrDayShiftComments) {
                setDayShiftComments(currentDsrLog.dsrDayShiftComments);
            }
            if (currentDsrLog.dsrNightShiftComments) {
                setNightShiftComments(currentDsrLog.dsrNightShiftComments);
            }

            // Set skip states
            if (currentDsrLog.isSkippedDayShift !== undefined || currentDsrLog.isSkippedNightShift !== undefined) {
                setSkipped({
                    day: currentDsrLog.isSkippedDayShift || false,
                    night: currentDsrLog.isSkippedNightShift || false,
                    all: (currentDsrLog.isSkippedDayShift && currentDsrLog.isSkippedNightShift) || false
                });
            }

            // Set existing signatures if available (handle both base64 and S3 URLs)
            if (currentDsrLog.managerImageData) {
            // Base64 data
                setSignatureData(prev => ({ ...prev, manager: `data:image/png;base64,${currentDsrLog.managerImageData}` }));
            } else if (currentDsrLog.managerSignatureS3Object && currentDsrLog.managerSignatureS3Object.objectUrl) {
                // S3 URL
                setSignatureData(prev => ({ ...prev, manager: currentDsrLog.managerSignatureS3Object.objectUrl }));
            }

            if (currentDsrLog.clientImageData) {
            // Base64 data
                setSignatureData(prev => ({ ...prev, client: `data:image/png;base64,${currentDsrLog.clientImageData}` }));
            } else if (currentDsrLog.clientSignatureS3Object && currentDsrLog.clientSignatureS3Object.objectUrl) {
                // S3 URL
                setSignatureData(prev => ({ ...prev, client: currentDsrLog.clientSignatureS3Object.objectUrl }));
            }

            // Check if report is already generated
            if (currentDsrLog.reportDetails && currentDsrLog.reportDetails.fileUrl) {
                setIsReportGenerated(true);
                setPdfUrl(currentDsrLog.reportDetails.fileUrl);
            } else {
                setIsReportGenerated(false);
                setPdfUrl('');
            }
        }
    }, [currentDsrLog]);

    // Update weather display when active shift changes
    useEffect(() => {
        if (activeShift === 'day') {
            setWeather(dayShiftWeather);
        } else {
            setWeather(nightShiftWeather);
        }
    }, [activeShift, dayShiftWeather, nightShiftWeather]);

    const openSignaturePad = (type, event) => {
        if (event?.preventDefault) event.preventDefault();
        if (event?.stopPropagation) event.stopPropagation();
        setSignatureType(type);
        setSignatureDrawerOpen(true);
        if (type === 'manager' && validationErrors.managerSignature) {
            setValidationErrors(prev => ({ ...prev, managerSignature: undefined }));
        }
    };

    const closeSignaturePad = () => {
        setSignatureDrawerOpen(false);
        setSignatureType(null);
    };

    // Validation function to check all mandatory fields
    const validateForm = () => {
        const errors = {};

        // Comments required only for shifts that are not skipped
        const commentParts = [];
        if (!skipped.day && (!dayShiftComments || dayShiftComments.trim() === '')) commentParts.push('Day shift');
        if (!skipped.night && (!nightShiftComments || nightShiftComments.trim() === '')) commentParts.push('Night shift');
        if (commentParts.length > 0) {
            errors.comments = `Comments are required for ${commentParts.join(' and ')} before submit.`;
        }

        // Weather required only when that shift is not skipped
        const weatherParts = [];
        if (!skipped.day && !dayShiftWeather) weatherParts.push('Day shift');
        if (!skipped.night && !nightShiftWeather) weatherParts.push('Night shift');
        if (weatherParts.length > 0) {
            errors.weather = `Weather selection is required for ${weatherParts.join(' and ')}`;
            errors.weather = `Weather selection is required for ${weatherParts.join(' and ')}.`;
        }

        // Check manager name
        const managerName = form.getFieldValue('managerName');
        if (!managerName || managerName.trim() === '') {
            errors.managerName = 'Manager name is required';
        }

        // Check manager signature
        if (!signatureData.manager) {
            errors.managerSignature = 'Manager signature is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Add validation function to check if all mandatory fields are filled
    const isFormValid = () => {
        // Comments required only for shifts that are not skipped
        if (!skipped.day && (!dayShiftComments || dayShiftComments.trim() === '')) return false;
        if (!skipped.night && (!nightShiftComments || nightShiftComments.trim() === '')) return false;

        // Weather required for each shift that is not skipped
        if (!skipped.day && !dayShiftWeather) return false;
        if (!skipped.night && !nightShiftWeather) return false;

        // Manager name and signature always required for final submit
        const managerName = form.getFieldValue('managerName');
        if (!managerName || managerName.trim() === '') return false;
        if (!signatureData.manager) return false;

        return true;
    };

    const handleFinish = async (values) => {
        if (!dsrLogId) {
            message.error('DSR log not initialized. Please try again.');
            return;
        }

        // Validate form before submission
        if (!validateForm()) {
            const missingFields = [];
            if (!skipped.day && (!dayShiftComments || dayShiftComments.trim() === '')) {
                missingFields.push('Day Shift Comments');
            }
            if (!skipped.night && (!nightShiftComments || nightShiftComments.trim() === '')) {
                missingFields.push('Night Shift Comments');
            }
            if (!skipped.day && !dayShiftWeather) {
                missingFields.push('Day Shift Weather');
            }
            if (!skipped.night && !nightShiftWeather) {
                missingFields.push('Night Shift Weather');
            }
            const managerName = form.getFieldValue('managerName');
            if (!managerName || managerName.trim() === '') {
                missingFields.push('Manager Name');
            }
            if (!signatureData.manager) {
                missingFields.push('Manager Signature');
            }

            const errorMessage = `Please fill the following required fields: ${missingFields.join(', ')}`;
            message.error(errorMessage);
            return;
        }

        // Prepare the DSR log data according to the API structure
        const dsrLogData = {
            id: dsrLogId,
            sowId: selectedMainline.id,
            projectId: selectedProject.id,
            logCreatedBy: currentDsrLog.logCreatedBy,
            logDate: selectedDateValue.format('YYYY-MM-DD'),
            logSubmitDate: moment().toISOString(),
            weatherStatusDayshift: dayShiftWeather ? parseInt(dayShiftWeather) : null,
            weatherStatusNightshift: nightShiftWeather ? parseInt(nightShiftWeather) : null,
            managerImageData: signatureData.manager ? signatureData.manager.split(',')[1] : null,
            managerSignatureS3Object: signatureData.manager ? {
                contentType: "image/png",
                orginalFilename: `Manager_Signature_${Date.now()}.png`
            } : null,
            clientImageData: signatureData.client ? signatureData.client.split(',')[1] : null,
            clientSignatureS3Object: signatureData.client ? {
                contentType: "image/png",
                orginalFilename: `Client_Signature_${Date.now()}.png`
            } : null,
            managerName: values.managerName || '',
            clientName: values.clientName || '',
            dsrNightShiftComments: nightShiftComments,
            dsrDayShiftComments: dayShiftComments
        };

        try {
            const waitForSaga = (dispatchFn, payload) =>
                new Promise((resolve, reject) => {
                    dispatchFn({ ...payload, deferred: { resolve, reject } });
                });

            await waitForSaga(onSubmitDsrLog, { dsrLogData });

            const sectionsSkipped = {};
            const submissionStatus = {};

            reportSections.forEach(section => {
                const dayShiftKey = `${section.skipKey}DayShift`;
                const nightShiftKey = `${section.skipKey}NightShift`;

                const daySkipState = currentDsrLog.sectionsSkipped?.[dayShiftKey] === 1;
                sectionsSkipped[dayShiftKey] = daySkipState ? 1 : 0;

                const nightSkipState = currentDsrLog.sectionsSkipped?.[nightShiftKey] === 1;
                sectionsSkipped[nightShiftKey] = nightSkipState ? 1 : 0;

                const daySubmissionStatus = generateSubmissionStatus(section.key, 'day', daySkipState);
                const nightSubmissionStatus = generateSubmissionStatus(section.key, 'night', nightSkipState);

                Object.assign(submissionStatus, daySubmissionStatus, nightSubmissionStatus);
            });

            await waitForSaga(onUpdateDsrLogSections, {
                dsrLogId,
                sectionsSkipped,
                submissionStatus,
            });

            await waitForSaga(onCreateDsrLog, {
                projectId: selectedProject.id,
                logDate: selectedDateValue.format('YYYY-MM-DD'),
                sowId: selectedMainline.id,
            });

            message.success(
                finalIsSubmitted
                    ? 'DSR report resubmitted successfully!'
                    : 'DSR report submitted successfully!'
            );
            setValidationErrors({});

        } catch (error) {
            console.error('Error submitting DSR report:', error);
            message.error('Failed to submit DSR report. Please try again.');
        }
    };

    const handleDSRSignatureSave = (base64Image) => {
        setSignatureData(prev => ({ ...prev, [signatureType]: base64Image }));
        // Clear validation error for this signature when added
        if (signatureType === 'manager' && validationErrors.managerSignature) {
            setValidationErrors(prev => ({ ...prev, managerSignature: undefined }));
        }
        closeSignaturePad();
    };

    const onWeatherChange = (value) => {
        const normalized = value === null || value === undefined || value === '' ? undefined : value;
        if (activeShift === 'day') {
            setDayShiftWeather(normalized);
        } else {
            setNightShiftWeather(normalized);
        }
        setWeather(normalized);
    };

    const onWeatherSearch = (value) => {
        console.log('search:', value);
    };

    const onAddNewWeather = (e) => {
        setNewWeather(e.target.value);
    };

    const addWeatherItem = () => {
        if (newWeather) {
            weatherOptions.push({ value: newWeather, label: newWeather });
            setNewWeather('');
        }
    };

    const removeWeatherItem = () => {
        weatherOptions.pop();
    };

    const handleCommentsChange = (value) => {
        if (activeShift === 'day') {
            setDayShiftComments(value);
        } else {
            setNightShiftComments(value);
        }
        // Clear validation error when comments are entered
        if (validationErrors.comments) {
            setValidationErrors(prev => ({ ...prev, comments: undefined }));
        }
    };

    const getCurrentWeather = () => {
        return activeShift === 'day' ? dayShiftWeather : nightShiftWeather;
    };

    const getCurrentComments = () => {
        return activeShift === 'day' ? dayShiftComments : nightShiftComments;
    };

    const getWeatherLabel = (value) => {
        const option = weatherOptions.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDateValue(date);
            if (onSubmit) onSubmit(date);
        }
    };

    // Check if a section is skipped for the current shift - simplified
    const isSectionSkipped = (sectionKey) => {
        return getSectionSkipState(sectionKey);
    };

    const handleGeneratePdf = async () => {
        if (!currentDsrLog?.id || !selectedProject?.id) {
            message.error('DSR log or project not available. Please try again.');
            return;
        }

        setIsGeneratingReport(true);
        try {
            const dsrLogId = currentDsrLog.id;
            const projectId = selectedProject.id;
            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/invoke-generate-report?dsrLogId=${dsrLogId}&projectId=${projectId}&generateFlashReport=1&generatePdfReport=1`;
            const authToken = await getJwtToken();

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to generate report');
            }

            const data = await response.json();
            setIsReportGenerated(true);
            message.success('Report generated successfully!');

            // If the API returns a PDF URL, show it in modal
            if (data.pdfUrl) {
                setPdfUrl(data.pdfUrl);
                setShowPdfModal(true);
            }
        } catch (err) {
            console.error('Error generating report:', err);
            message.error('Failed to generate report. Please try again.');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // Generate modal email helper functions (for PDF generation/sharing)
    const handleEmailInputChange = (e) => setEmailInput(e.target.value);

    const handleAddEmail = () => {
        const emails = emailInput.split(',').map(e => e.trim()).filter(e => e);
        setEmailList(prev => [...new Set([...prev, ...emails])]);
        setEmailInput('');
    };

    const handleRemoveEmail = (email) => {
        setEmailList(prev => prev.filter(e => e !== email));
    };

    const openGenerateModal = () => {
        setIsGenerateModalVisible(true);
    };

    const closeGenerateModal = () => {
        setIsGenerateModalVisible(false);
        setEmailList([]);
        setEmailInput('');
    };

    const getCurrentUserEmail = () => {
        try {
            const raw = localStorage.getItem('user');
            if (!raw) return '';
            const u = JSON.parse(raw);
            return String(u?.email || '').trim();
        } catch {
            return '';
        }
    };

    const handleGenerateSelectedReport = async () => {
        if (selectedReportType === 'flash') {
            await handleGenerateFlashReportClick();
            setIsGenerateModalVisible(false);
            return;
        }

        if (selectedReportType === 'combined') {
            await handleGenerateCombinedReportClick();
            setIsGenerateModalVisible(false);
            return;
        }

        // PDF report:
        // - If emails are provided, generate + share (GET invoke-generate-report with emailToDestinations)
        // - Otherwise, generate only (same API without email)
        if (!currentDsrLog?.id || !selectedProject?.id) {
            message.error('DSR log or project not available. Please try again.');
            return;
        }

        // Share flow
        if (emailList.length > 0) {
            setIsGeneratingReport(true);
            try {
                const authToken = await getJwtToken();
                const dsrLogIdLocal = currentDsrLog?.id;
                const projectIdLocal = selectedProject?.id;
                const emailToDestinations = encodeURIComponent(emailList.join(','));
                const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/invoke-generate-report?dsrLogId=${dsrLogIdLocal}&projectId=${projectIdLocal}&generateFlashReport=1&generatePdfReport=1&emailToDestinations=${emailToDestinations}`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': authToken,
                    },
                });

                if (!response.ok) throw new Error('Failed to generate and share report');

                message.success('Report generated and shared successfully!');
                closeGenerateModal();
            } catch (err) {
                console.error('Error sharing report:', err);
                message.error('Failed to share report. Please try again.');
            } finally {
                setIsGeneratingReport(false);
            }
            return;
        }

        // Generate-only flow (may open PDF viewer modal if backend returns URL)
        await handleGeneratePdf();
        setIsGenerateModalVisible(false);
    };

    const handleGenerateCombinedReportClick = async () => {
        const projectId = selectedProject?.id;
        if (!projectId) {
            message.error('Project is required to generate combined report');
            return;
        }
        const userEmail = getCurrentUserEmail();
        if (!userEmail) {
            message.error('User email not found. Please login again.');
            return;
        }

        const formattedLogDate = moment(selectedDateValue || selectedDate || new Date()).format('YYYY-MM-DD');
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.info('[DSR combined report] handleGenerateCombinedReportClick', {
                projectId,
                logDate: formattedLogDate,
                hasUserEmail: Boolean(userEmail),
            });
        }
        setIsGeneratingCombined(true);
        try {
            const url = `${process.env.REACT_APP_BASE_URL}/project-dpr-dsr-summary/generate`;
            const payload = {
                projectId,
                logDate: formattedLogDate,
                emailTo: [userEmail]
            };
            const authToken = await getJwtToken();
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => null);
            if (!response.ok || (data && data.success === false)) {
                const backendMsg = data?.error || data?.message || 'Failed to generate combined report';
                message.error(backendMsg);
                return;
            }

            message.success('Combined report generation started (sent to your email).');
        } catch (err) {
            console.error('Error generating combined report:', err);
            message.error('Failed to generate combined report. Please try again.');
        } finally {
            setIsGeneratingCombined(false);
        }
    };

    const handleGenerateFlashReportClick = async() => {
        try {
            const dsrLogId = currentDsrLog?.id;
            if (!dsrLogId) {
                message.error('DSR log is not ready yet. Please wait a moment and try again.');
                return;
            }

            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/generate-shift-flash-report`;
            const payload = {
             dsrLogId,
             shift: activeShift === 'day' ? 0 : 1,
            };

            // Get authentication token
            const authToken = await getJwtToken();

            const response = await fetch(url, {
             method: 'POST',
             headers: {
                 'Content-Type': 'application/json',
                 'Authorization': authToken,
             },
             body: JSON.stringify(payload),
            });
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(errorText || `Request failed (${response.status})`);
            }
            
            await response.json().catch(() => null);
            message.success('Flash report generated successfully!');
        } catch (error) {
            console.error('Error generating flash report:', error);
            message.error(`Failed to generate flash report: ${error.message}`);
        }
     };

    return (
        <div className="dsr-report-details modern-dsr-report-details">
            {/* Read-only indicator */}
            {/* {finalIsSubmitted && (
                <div style={{
                    background: 'rgba(255, 77, 79, 0.1)',
                    border: '1px solid #ff4d4f',
                    borderRadius: 6,
                    padding: '12px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <CheckCircle2 color="#ff4d4f" size={16} aria-hidden />
                    <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        DSR Report has been submitted. This is a read-only view.
                    </span>
                </div>
            )} */}

            <Tabs
                activeKey={activeShift}
                onChange={setActiveShift}
                className="custom-tab modern-dsr-shift-tabs"
            >
                <TabPane tab="Day Shift" key="day" />
                <TabPane tab="Night Shift" key="night" />
            </Tabs>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col>
                    <div className="modern-dsr-weather-field">
                        <Select
                            className="weather-select modern-dsr-weather-select"
                            placeholder="Select weather"
                            dropdownClassName="modern-dsr-weather-dropdown"
                            getPopupContainer={() => document.body}
                            onChange={(value) => {
                                onWeatherChange(value);
                                if (validationErrors.weather) {
                                    setValidationErrors(prev => ({ ...prev, weather: undefined }));
                                }
                            }}
                            value={(() => {
                                const w = getCurrentWeather();
                                return w !== undefined && w !== null && w !== '' ? w : undefined;
                            })()}
                            style={{ minWidth: 260 }}
                            suffixIcon={
                                getCurrentWeather() ? (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Clear weather"
                                        onMouseDown={(e) => {
                                            // Select opens on mousedown; preventDefault + clear here so it always runs.
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onWeatherChange(undefined);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onWeatherChange(undefined);
                                            }
                                        }}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            color: '#64748b',
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                    >
                                        <X size={16} aria-hidden />
                                    </span>
                                ) : undefined
                            }
                            disabled={activeShift === 'day' ? skipped.day : skipped.night}
                        >
                            {weatherOptions.map((opt, i) => {
                                const WIcon = opt.Icon;
                                return (
                                    <Option key={i} value={opt.value}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <WIcon size={WEATHER_ICON_SIZE} color={opt.iconColor} strokeWidth={2} aria-hidden />
                                            <span>{opt.label}</span>
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                        {validationErrors.weather && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                                {validationErrors.weather}
                            </div>
                        )}
                        <span className="modern-dsr-weather-required" style={{ color: '#dc2626', fontWeight: 600 }}>
                            *
                        </span>
                    </div>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {reportSections.map(section => {
                    const isSkipped = isSectionSkipped(section.key);
                    const isShiftSkipped = activeShift === 'day' ? skipped.day : skipped.night;
                    const isSectionSubmitted = getSectionSubmittedState(section.key);

                    return (
                        <Col xs={24} sm={12} md={8} lg={6} key={section.key}>
                            <Card
                                hoverable
                                className="modern-dsr-section-card"
                                onClick={() => {
                                    console.log('Card clicked:', section.key);
                                    if (onSectionSelect) onSectionSelect(section.key);
                                }}
                                style={{
                                    border: activeShift === section.key ? '2px solid #1890ff' : undefined,
                                    position: 'relative',
                                    minHeight: 40,
                                    opacity: isSkipped || isShiftSkipped ? 0.6 : 1,
                                }}
                            >
                                <div
                                    className="skip-checkbox-container modern-dsr-card-skip-wrap"
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            zIndex: 2,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 8,
                                            margin: 4,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        role="presentation"
                                    >
                                        <CrceCheckbox
                                            label="Skip"
                                            checked={isSkipped}
                                            onChange={(v) => {
                                                const checked = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                                                console.log('Checkbox onChange triggered:', section.key, checked);
                                                handleSectionSkipChange(section.key, checked);
                                            }}
                                            disabled={updatingSection === section.key || isShiftSkipped}
                                            borderColor="#00a88e"
                                        />
                                        {updatingSection === section.key && (
                                            <Spin
                                                indicator={(
                                                    <span className="modern-dsr-inline-spin" aria-hidden>
                                                        <Loader2 size={14} className="modern-dsr-spin-svg" />
                                                    </span>
                                                )}
                                                size="small"
                                            />
                                        )}
                                    </div>

                                {isSectionSubmitted && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 24,
                                            height: 24,
                                        }}
                                    >
                                        <CheckCircle2 className="modern-dsr-submitted-icon" size={22} strokeWidth={2} aria-hidden />
                                    </div>
                                )}

                                <div
                                    style={{
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        minHeight: 30,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 50,
                                        paddingLeft: 60,
                                        paddingRight: isSectionSubmitted ? 40 : 0,
                                        gap: 4,
                                    }}
                                >
                                    <div className="modern-dsr-section-title">{section.title}</div>

                                    {(isSkipped || isShiftSkipped) && (
                                        <div className="modern-dsr-skipped-pill">
                                            Skipped
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    );
                })}
                {/* PQT Actual Values - part of DSR log */}
            </Row>

            {/* Global Form — `component={false}` avoids a native <form>; CrceButton renders <button>
                without type="button", which would otherwise default to submit inside <form> and fire
                onFinish when clicking e.g. "Add Signature". */}
            <Form
                form={form}
                component={false}
                layout="vertical"
                onFinish={handleFinish}
            >
                <Form.Item
                    label={`Comments (${activeShift === 'day' ? 'Day' : 'Night'} Shift)`}
                    className={(activeShift === 'day' ? !skipped.day : !skipped.night) ? 'mandatory-field' : ''}
                >
                    <TextArea
                        rows={3}
                        placeholder={`Enter ${activeShift} shift comments...`}
                        value={getCurrentComments()}
                        onChange={(e) => handleCommentsChange(e.target.value)}
                        className={validationErrors.comments ? 'validation-error' : ''}
                    />
                    {validationErrors.comments && (
                        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                            {validationErrors.comments}
                        </div>
                    )}
                </Form.Item>

                {/* Manager/Client Name Row */}
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Manager Name"
                            name="managerName"
                            className="mandatory-field"
                        >
                            <Input
                                placeholder="Manager Name"
                                style={{ height: '60px' }}
                                className={validationErrors.managerName ? 'validation-error' : ''}
                                onChange={(e) => {
                                    // Clear validation error when user starts typing
                                    if (validationErrors.managerName) {
                                        setValidationErrors(prev => ({ ...prev, managerName: undefined }));
                                    }
                                }}
                            />
                        </Form.Item>
                        {validationErrors.managerName && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: -16, marginBottom: 16 }}>
                                {validationErrors.managerName}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Client Name" name="clientName">
                            <Input
                                placeholder="Client Name"
                                style={{ height: '60px' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Manager/Client Signature Row */}
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Manager Signature"
                            name="managerSignature"
                            className="mandatory-field"
                        >
                            {!signatureData.manager ? (
                                <div
                                    className={`modern-dsr-sig-placeholder ${validationErrors.managerSignature ? 'validation-error' : ''}`}
                                >
                                    <CrceButton
                                        label="Add Signature"
                                        variant="tertiary"
                                        size="medium"
                                        icon={PenLine}
                                        onClick={(e) => openSignaturePad('manager', e)}
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        padding: 8,
                                    }}
                                    onClick={(e) => openSignaturePad('manager', e)}
                                >
                                        <img
                                            src={signatureData.manager}
                                            alt="Manager Signature"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Form.Item>
                        {validationErrors.managerSignature && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: -16, marginBottom: 16 }}>
                                {validationErrors.managerSignature}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Client Signature" name="clientSignature">
                            {!signatureData.client ? (
                                <div className="modern-dsr-sig-placeholder">
                                    <CrceButton
                                        label="Add Signature"
                                        variant="tertiary"
                                        size="medium"
                                        icon={PenLine}
                                        onClick={(e) => openSignaturePad('client', e)}
                                    />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        padding: 8,
                                    }}
                                    onClick={(e) => openSignaturePad('client', e)}
                                >
                                        <img
                                            src={signatureData.client}
                                            alt="Client Signature"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <div className="modern-dsr-form-footer">
                <CrceButton
                    label="Cancel"
                    variant="tertiary"
                    size="medium"
                    onClick={() => {
                        form.resetFields();
                        setSignatureData({ manager: null, client: null });
                        setDayShiftComments('');
                        setNightShiftComments('');
                        setValidationErrors({});
                        if (onSkip) onSkip();
                    }}
                />

                <CrceButton
                    label={
                        isSubmittingDsrLog
                            ? (finalIsSubmitted ? 'Resubmitting…' : 'Submitting…')
                            : (finalIsSubmitted ? 'Resubmit' : 'Submit')
                    }
                    variant="primary"
                    size="medium"
                    onClick={() => form.submit()}
                    disabled={
                        finalIsSubmitted
                            ? (isCreatingDsrLog || isSubmittingDsrLog)
                            : (isCreatingDsrLog || !isFormValid() || isSubmittingDsrLog)
                    }
                />
                <CrceButton
                    label={isGeneratingReport || isGeneratingCombined ? 'Generating…' : 'Generate Report'}
                    variant="primary"
                    size="medium"
                    icon={FileText}
                    onClick={openGenerateModal}
                    disabled={
                        !finalIsSubmitted
                        || (activeShift === 'day' ? skipped.day : skipped.night)
                        || isCreatingDsrLog
                        || !currentDsrLog?.id
                        || isGeneratingReport
                        || isGeneratingCombined
                    }
                />
            </div>

            <Drawer
                title="Signature"
                placement="right"
                onClose={closeSignaturePad}
                open={signatureDrawerOpen}
                width="32%"
                className="pipeline-modal Filter-Drawer signature-canvas"
            >
                <div className="drawer-content">
                    <Layout className="content-scrollable">
                        <Content>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', backgroundColor: 'transparent' }}>
                                <DSRSignaturePad
                                    visible={signatureDrawerOpen}
                                    onClose={closeSignaturePad}
                                    onSave={handleDSRSignatureSave}
                                    title={signatureType === 'manager' ? 'Manager' : 'Client'}
                                />
                            </div>
                        </Content>
                    </Layout>
                    <div className="ps-3 add-new-calibration-filter">
                        <Footer className="fixed-footer">
                            <div style={{ marginTop: 16, marginLeft: 10 }}>
                                <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={closeSignaturePad} />
                            </div>
                        </Footer>
                    </div>
                </div>
            </Drawer>

            <Modal
                visible={showPdfModal}
                onCancel={() => setShowPdfModal(false)}
                footer={null}
                width="80vw"
                className="modern-dsr-dt-add-modal modern-generate-report-modal"
                wrapClassName="modern-dsr-dt-add-modal__wrap"
            >
                <iframe src={pdfUrl} style={{ width: '100%', height: '80vh', border: 'none' }} />
            </Modal>

            <Modal
                title={<h3 className="modern-dsr-dt-add-modal__h-title">Generate Report</h3>}
                visible={isGenerateModalVisible}
                onCancel={closeGenerateModal}
                centered
                width={520}
                className="modern-dsr-dt-add-modal modern-generate-report-modal"
                wrapClassName="modern-dsr-dt-add-modal__wrap"
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                        <CrceButton
                            label="Cancel"
                            variant="tertiary"
                            size="medium"
                            onClick={closeGenerateModal}
                            disabled={isGeneratingReport || isGeneratingCombined}
                        />
                        <CrceButton
                            label={isGeneratingReport || isGeneratingCombined ? 'Generating…' : 'Generate'}
                            variant="primary"
                            size="medium"
                            onClick={handleGenerateSelectedReport}
                            disabled={
                                isGeneratingReport
                                || isGeneratingCombined
                            }
                        />
                    </div>
                )}
            >
                <div className="modern-dsr-dt-add-modal__form-scope">
                    <div style={{ marginBottom: 14 }}>
                        <div className="generate-report-section-label">Report type</div>
                        <Radio.Group
                            className="report-radio"
                            value={selectedReportType}
                            onChange={(e) => setSelectedReportType(e.target.value)}
                            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                        >
                            <Radio value="flash">Shift Flash Report (current shift)</Radio>
                            <Radio value="pdf">PDF Report</Radio>
                            <Radio value="combined">
                                Combined DPR+DSR Summary (sent to your email)
                            </Radio>
                        </Radio.Group>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        {selectedReportType === 'pdf' && (
                            <>
                                <span className="generate-report-section-label">Email (optional, for sharing)</span>
                                <Input
                                    placeholder="Enter emails followed by comma (,)"
                                    value={emailInput}
                                    onChange={handleEmailInputChange}
                                    onPressEnter={handleAddEmail}
                                    disabled={isGeneratingReport}
                                />
                                <div style={{ marginTop: 10 }}>
                                    <CrceButton
                                        label="Add"
                                        variant="primary"
                                        size="medium"
                                        onClick={handleAddEmail}
                                        disabled={!emailInput.trim() || isGeneratingReport}
                                    />
                                </div>
                                <div className="generate-report-hint">
                                    eg: 1234@mail.com, 234@email.com
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    {emailList.map(email => (
                                        <span key={email} style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            {email}
                                            <button
                                                type="button"
                                                className="modern-dsr-email-chip-remove"
                                                onClick={() => handleRemoveEmail(email)}
                                                disabled={isGeneratingReport}
                                                aria-label={`Remove ${email}`}
                                            >
                                                <X size={14} aria-hidden />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Combined report is generated directly (no email modal) */}
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => {
    const isCreatingDsrLog = get(dsrReducer, 'isCreatingDsrLog', false);
    const isSubmittingDsrLog = get(dsrReducer, 'isSubmittingDsrLog', false);
    const isUpdatingDsrLogSections = get(dsrReducer, 'isUpdatingDsrLogSections', false);
    const currentDsrLog = get(dsrReducer, 'currentDsrLog', null);

    return {
        isCreatingDsrLog,
        isSubmittingDsrLog,
        isUpdatingDsrLogSections,
        currentDsrLog
    };
};

const mapDispatchToProps = {
    onCreateDsrLog: createDsrLog,
    onSubmitDsrLog: submitDsrLog,
    onUpdateDsrLogSections: updateDsrLogSections
};

export default connect(mapStateToProps, mapDispatchToProps)(DSRReportDetails); 
